import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';

import { TranslatePipe } from '../core/pipes/translate.pipe';
import { LanguageService } from '../core/services/language.service';
import { SubscriptionContactInfo, SubscriptionPackage, SubscriptionService, SubscriptionUsageSummary } from '../core/services';
import { Nav } from '../nav/nav';
import { TopHeader } from '../top-header/top-header';

type LocalizedCopy = {
  bn: string;
  en: string;
};

type SubscriptionDisplayPlan = SubscriptionPackage & {
  isCustomCard?: boolean;
  customDescription?: LocalizedCopy;
};

@Component({
  selector: 'app-subscription',
  standalone: true,
  imports: [CommonModule, Nav, TopHeader, TranslatePipe],
  templateUrl: './subscription.html',
  styleUrl: './subscription.css'
})
export class SubscriptionComponent implements OnInit {
  isLoading = false;
  error: string | null = null;
  contactInfo: SubscriptionContactInfo = { mobileWhatsapp: [], email: [] };
  packages: SubscriptionPackage[] = [];
  displayPackages: SubscriptionDisplayPlan[] = [];
  usageSummary: SubscriptionUsageSummary | null = null;
  copiedContactKey: string | null = null;
  private copiedContactTimeoutId: number | null = null;

  constructor(
    private readonly subscriptionService: SubscriptionService,
    private readonly language: LanguageService
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    forkJoin({
      info: this.subscriptionService.getSubscriptionInfo(),
      summary: this.subscriptionService.getSubscriptionSummary()
    }).subscribe({
      next: ({ info, summary }) => {
        this.contactInfo = info.contactInfo;
        this.packages = info.packages.filter((plan) => Number(plan?.status ?? 0) === 1);
        this.usageSummary = summary;
        this.displayPackages = this.buildDisplayPackages(this.packages, summary);
        this.isLoading = false;
      },
      error: (err: any) => {
        this.error = err?.message || 'Failed to load subscription info.';
        this.isLoading = false;
      }
    });
  }

  isCurrentPackage(plan: SubscriptionPackage): boolean {
    const currentPackageName = `${this.usageSummary?.currentPackage?.packageName ?? ''}`.trim().toLowerCase();
    const planName = `${plan?.packageName ?? ''}`.trim().toLowerCase();
    return !!currentPackageName && !!planName && currentPackageName === planName;
  }

  isCustomCard(plan: SubscriptionDisplayPlan): boolean {
    return !!plan?.isCustomCard;
  }

  isFreePlan(plan: SubscriptionPackage): boolean {
    return Number(plan?.priceMonthly ?? 0) <= 0 && Number(plan?.priceYearly ?? 0) <= 0;
  }

  isCurrentPackageTrial(): boolean {
    return this.usageSummary?.currentPackage?.isTrial === true;
  }

  formatPrice(value: number): string {
    const amount = Number.isFinite(value) ? value : 0;
    const hasFraction = Math.abs(amount % 1) > 0;
    return `\u09F3${new Intl.NumberFormat('en-BD', {
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: hasFraction ? 2 : 0
    }).format(amount)}`;
  }

  formatStorage(value: number): string {
    const memory = Number.isFinite(value) ? value : 0;
    if (memory >= 1024) {
      const gb = memory / 1024;
      return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
    }
    return `${memory} MB`;
  }

  formatSubuserCount(value: number): string {
    const count = Number.isFinite(value) ? value : 0;
    if (this.language.currentLanguage === 'bn') {
      return `${count} সাবইউজার`;
    }
    return `${count} ${count === 1 ? 'subuser' : 'subusers'}`;
  }

  localize(copy?: LocalizedCopy | null): string {
    if (!copy) {
      return '';
    }
    return this.language.currentLanguage === 'bn' ? copy.bn : copy.en;
  }

  currentPackageExpireDate(plan: SubscriptionPackage): string {
    if (!this.isCurrentPackage(plan)) {
      return '';
    }

    const raw = `${this.usageSummary?.currentPackage?.expireDate ?? ''}`.trim();
    if (!raw) {
      return '';
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
      return raw;
    }

    return parsed.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  heroSubtitle(): string {
    const freePlan = this.getFreePlan();
    const flatCount = Number(freePlan?.totalFlat ?? 0);
    if (flatCount > 0) {
      return this.language.currentLanguage === 'bn'
        ? `ফ্রি প্যাকেজে ${flatCount} টি ফ্ল্যাট দিয়ে শুরু করুন। পরে প্রয়োজন হলে ফ্ল্যাট লিমিট অনুযায়ী আপগ্রেড করুন।`
        : `Start with the Free plan for ${flatCount} flats. Upgrade by flat limit when you need more. contact for free trail of any package.`;
    }

    return this.language.currentLanguage === 'bn'
      ? 'ফ্রি প্যাকেজ দিয়ে শুরু করুন। পরে প্রয়োজন হলে ফ্ল্যাট লিমিট অনুযায়ী আপগ্রেড করুন।'
      : 'Start with the Free plan. Upgrade by flat limit when you need more. contact for free trail of any package.';
  }

  heroSubtitleLead(): string {
    if (this.language.currentLanguage === 'bn') {
      return this.heroSubtitle();
    }
    return this.heroSubtitle().replace(/\s*contact for free trail of any package\.\s*$/i, '').trim();
  }

  heroSubtitleTrailNote(): string {
    return this.language.currentLanguage === 'bn'
      ? ''
      : 'Contact for free trail.';
  }

  heroSubtitleTrailLead(): string {
    return this.language.currentLanguage === 'bn'
      ? 'যেকোনো প্যাকেজের '
      : 'Contact for ';
  }

  heroSubtitleTrailHighlight(): string {
    return this.language.currentLanguage === 'bn'
      ? 'ফ্রি ট্রায়ালের'
      : 'Free Trail';
  }

  heroSubtitleTrailSuffix(): string {
    return this.language.currentLanguage === 'bn'
      ? ' জন্য যোগাযোগ করুন'
      : ' of any package';
  }

  policyUpgradeText(): string {
    const freePlan = this.getFreePlan();
    const flatCount = Number(freePlan?.totalFlat ?? 0);
    if (flatCount > 0) {
      return this.language.currentLanguage === 'bn'
        ? `ফ্রি প্যাকেজে ${flatCount} টি ফ্ল্যাট পর্যন্ত ব্যবহার করা যাবে। এরপর প্রয়োজন হলে সাবস্ক্রিপশন নিতে হবে।`
        : `The Free package allows up to ${flatCount} flats. Take a subscription when you need more.`;
    }

    return this.language.currentLanguage === 'bn'
      ? 'ফ্রি প্যাকেজের লিমিট শেষ হলে সাবস্ক্রিপশন নিতে হবে।'
      : 'Take a subscription when the Free package limit is reached.';
  }

  async copyContact(value: string, type: 'mobile' | 'email'): Promise<void> {
    const text = `${value ?? ''}`.trim();
    if (!text) {
      return;
    }

    const copied = await this.copyText(text);
    if (!copied) {
      return;
    }

    this.copiedContactKey = `${type}:${text}`;
    if (this.copiedContactTimeoutId !== null) {
      window.clearTimeout(this.copiedContactTimeoutId);
    }
    this.copiedContactTimeoutId = window.setTimeout(() => {
      this.copiedContactKey = null;
      this.copiedContactTimeoutId = null;
    }, 1500);
  }

  isContactCopied(value: string, type: 'mobile' | 'email'): boolean {
    return this.copiedContactKey === `${type}:${`${value ?? ''}`.trim()}`;
  }

  whatsappHref(mobile: string): string {
    const cleaned = `${mobile ?? ''}`.replace(/[^\d]/g, '');
    return `https://wa.me/${cleaned}`;
  }

  telHref(mobile: string): string {
    return `tel:${`${mobile ?? ''}`.replace(/\s+/g, '')}`;
  }

  mailtoHref(email: string): string {
    return `mailto:${`${email ?? ''}`.trim()}`;
  }

  private getFreePlan(): SubscriptionPackage | undefined {
    return this.packages.find((plan) => this.isFreePlan(plan))
      ?? this.packages.find((plan) => `${plan?.packageName ?? ''}`.trim().toLowerCase() === 'free');
  }

  private async copyText(value: string): Promise<boolean> {
    const text = `${value ?? ''}`.trim();
    if (!text) {
      return false;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // Fallback below for browsers that block async clipboard access.
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      return document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
  }

  private buildDisplayPackages(
    packages: SubscriptionPackage[],
    summary: SubscriptionUsageSummary | null
  ): SubscriptionDisplayPlan[] {
    const customName = 'custom';
    const visiblePackages = packages.filter((plan) => `${plan?.packageName ?? ''}`.trim().toLowerCase() !== customName);
    const currentPackageName = `${summary?.currentPackage?.packageName ?? ''}`.trim().toLowerCase();

    const hasCustomInApi = packages.some((plan) => `${plan?.packageName ?? ''}`.trim().toLowerCase() === customName);
    const currentIsCustom = currentPackageName === customName;

    if (!hasCustomInApi && !currentIsCustom) {
      return visiblePackages;
    }

    const currentPackage = summary?.currentPackage;
    const customPlan: SubscriptionDisplayPlan = {
      id: currentIsCustom ? Number(currentPackage?.id ?? 0) : -999,
      packageName: 'Custom',
      totalFlat: currentIsCustom ? Number(currentPackage?.maxActiveFlat ?? 0) : 0,
      totalRenter: currentIsCustom ? Number(currentPackage?.maxActiveRenter ?? 0) : 0,
      maxActiveEmployee: currentIsCustom ? Number(currentPackage?.maxActiveEmployee ?? 0) : 0,
      maxFileMemory: currentIsCustom ? Number(currentPackage?.maxFileStorage ?? 0) : 0,
      priceMonthly: 0,
      priceYearly: 0,
      status: 1,
      isCustomCard: true,
      customDescription: {
        bn: 'ফ্ল্যাট, ভাড়াটিয়া, সাবইউজার এবং ফাইল স্টোরেজ নিজের প্রয়োজন অনুযায়ী কাস্টমাইজ করা যাবে',
        en: 'You can customize flats, renters, subusers, and file storage based on your needs'
      }
    };

    return [...visiblePackages, customPlan];
  }
}

